import React, { useEffect, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Banknote, Download } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

function fmtPeriod(p) {
  if (!p) return "—";
  const [y, m] = p.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString([], { month: "long", year: "numeric" });
}

async function downloadPayslipPdf(p) {
  try {
    const token = getToken();
    const res = await fetch(`${API}/payslips/${p.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payslip_${(p.user_name || "me").replace(/\s+/g, "_")}_${p.period_month}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Could not download payslip PDF");
  }
}

export default function MyPayslips() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payslips/me");
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Compensation</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">My Payslips</h1>
        <p className="text-sm text-zinc-500 mt-2">Your monthly compensation history.</p>
      </div>

      {(() => {
        if (loading) return <div className="text-zinc-500 text-sm">Loading…</div>;
        if (items.length === 0) {
          return (
            <div className="bg-white border border-zinc-200 rounded-md p-8 text-center">
              <Banknote className="w-8 h-8 mx-auto text-zinc-300" />
              <div className="mt-2 text-zinc-500">No payslips yet.</div>
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((p) => (
              <div key={p.id} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`my-payslip-${p.period_month}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-xl font-bold tracking-tight">{fmtPeriod(p.period_month)}</div>
                  <div className="text-2xl font-bold font-mono text-emerald-700">{fmtINR(p.net_payable)}</div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs">
                  <span className="text-zinc-500">Salary</span><span className="font-mono text-right">{fmtINR(p.salary)}</span>
                  <span className="text-zinc-500">ESI</span><span className="font-mono text-right text-zinc-600">{fmtINR(p.esi)}</span>
                  <span className="text-zinc-500">PF</span><span className="font-mono text-right text-zinc-600">{fmtINR(p.pf)}</span>
                  <span className="text-zinc-500">PT</span><span className="font-mono text-right text-zinc-600">{fmtINR(p.pt)}</span>
                  {p.bonus > 0 && (<><span className="text-emerald-700">Bonus</span><span className="font-mono text-right text-emerald-700">+{fmtINR(p.bonus)}</span></>)}
                  {p.deduction > 0 && (<><span className="text-red-600">Deduction</span><span className="font-mono text-right text-red-600">−{fmtINR(p.deduction)}</span></>)}
                  <span className="text-zinc-500 border-t border-zinc-100 pt-1.5 mt-1">Gross (employer)</span>
                  <span className="font-mono text-right border-t border-zinc-100 pt-1.5 mt-1">{fmtINR(p.gross)}</span>
                </div>
                {p.note && <div className="mt-3 text-xs text-zinc-500 italic">{p.note}</div>}
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadPayslipPdf(p)}
                    data-testid={`download-payslip-${p.period_month}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}
